import React, { useEffect, useMemo, useRef } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useModalStore } from '@/store/modal';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { PLUS_MONTHLY_CREDITS_DISPLAY } from '@/constants/config';
import { trackConversionEvent, setCheckoutSourceDimension, CONVERSION_ACTION } from '@/constants/utils/analyticsEvents';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { getPlusFeature } from './plusFeatureContent';

const WAVE_HEIGHTS = [10, 20, 34, 22, 48, 64, 38, 76, 54, 32, 60, 44, 26, 38, 18, 10];

function FeatureArtwork({ feature, icon, styles, ink }) {
  return (
    <View style={styles.artwork} accessible={false} accessibilityElementsHidden importantForAccessibility="no-hide-descendants"
      {...(Platform.OS === 'web' ? { 'aria-hidden': true } : {})}>
      {feature === 'voice' ? (
        <View style={styles.wave}>
          {WAVE_HEIGHTS.map((height, index) => <View key={index} style={[styles.waveBar, { height, opacity: 0.25 + (1 - Math.abs(index - 7.5) / 8) * 0.75 }]} />)}
        </View>
      ) : (
        <View style={styles.artworkTile}><Icon name={icon} size={32} color={ink} light /></View>
      )}
    </View>
  );
}

export default function PlusFeatureModal({ onClose, feature = 'plus', source, allowCredits = false, modelName, onDimensionsChange }) {
  const { theme } = useTheme();
  const { isXS } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const dark = isDarkTheme(theme);
  const ink = dark ? '#F5F5F7' : '#1D1D1F';
  const styles = useMemo(() => createStyles(dark, isXS), [dark, isXS]);
  const baseContent = getPlusFeature(feature);
  const content = allowCredits ? {
    ...baseContent,
    ...(modelName ? { title: `Unlock ${modelName}.` } : {}),
    description: `${modelName || (feature === 'video' ? 'This video model' : 'This model')} is available with Plus or purchased credits. Choose the option that works for you.`,
    note: 'Free signup credits do not unlock this model.',
  } : baseContent;
  const router = useRouter();
  const { openModal } = useModalStore(state => state.actions);
  const proceeding = useRef(false);
  const surface = source || `plus_feature_${feature}`;

  useEffect(() => {
    proceeding.current = false;
    trackConversionEvent(CONVERSION_ACTION.PAYWALL_SHOWN, surface);
    setCheckoutSourceDimension(surface);
  }, [surface]);

  useEffect(() => {
    // Also works when embedded in an existing media-error modal shell.
    onDimensionsChange?.({ ...PlusFeatureModal.modalOptions, ...(allowCredits ? { height: 720 } : {}) });
  }, [onDimensionsChange, allowCredits]);

  const continueToPlans = () => {
    if (proceeding.current) return;
    proceeding.current = true;
    trackConversionEvent(CONVERSION_ACTION.PAYWALL_UPGRADE_CLICKED, surface);
    // Keep the existing native purchase screen and signed Mac edition routing.
    // Prices, restore, checkout, and purchase errors belong to those flows.
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      onClose?.();
      router.push('/(auth)/paywall');
    } else {
      openModal('PricingModal', {
        title: content.action,
        subtitle: content.description,
        source: surface,
      });
    }
  };

  const buyCredits = () => {
    if (proceeding.current) return;
    proceeding.current = true;
    trackConversionEvent(CONVERSION_ACTION.PAYWALL_CREDITS_CLICKED, surface);
    openModal('CreditsPurchaseModal', { initialTab: 'buy-credits' });
  };

  return (
    <View style={styles.container} testID="plus-feature-modal">
      <View style={styles.header}>
        <View style={styles.badge}><Text style={styles.badgeText}>{allowCredits ? 'MORE WAYS TO CREATE' : 'GAB AI PLUS'}</Text></View>
        <Pressable accessibilityRole="button" accessibilityLabel="Close Plus feature preview" onPress={onClose}
          style={({ hovered, pressed }) => [styles.close, (hovered || pressed) && styles.hover]}>
          <Icon name="xmark" size={16} color={ink} />
        </Pressable>
      </View>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.body} showsVerticalScrollIndicator={false}>
        <FeatureArtwork feature={feature} icon={content.icon} styles={styles} ink={ink} />
        <Text accessibilityRole="header" style={styles.title}>{content.title}</Text>
        <Text style={styles.description}>{content.description}</Text>
        <View style={styles.benefits}>
          {content.benefits.map(([icon, title, detail]) => (
            <View key={title} style={styles.benefit}>
              <View style={styles.benefitIcon}><Icon name={icon} size={19} color={ink} light /></View>
              <View style={styles.benefitCopy}>
                <Text style={styles.benefitTitle}>{title}</Text>
                <Text style={styles.detail}>{detail}</Text>
              </View>
            </View>
          ))}
        </View>
        <View style={styles.included}>
          <Icon name="circle-plus" size={17} color={ink} />
          <Text style={styles.includedText}>{allowCredits
            ? `With Plus: ${PLUS_MONTHLY_CREDITS_DISPLAY} credits each month.`
            : `All of Plus. Including ${PLUS_MONTHLY_CREDITS_DISPLAY} credits each month.`}</Text>
        </View>
        {!!content.note && <Text style={styles.note}>{content.note}</Text>}
      </ScrollView>
      <View style={[styles.footer, { paddingBottom: Math.max(insets.bottom, 12) }]}>
        <Pressable accessibilityRole="button" accessibilityLabel={content.action} accessibilityHint="Choose a Plus subscription plan"
          onPress={continueToPlans} style={({ hovered, pressed }) => [styles.primary, { opacity: pressed ? 0.75 : hovered ? 0.9 : 1 }]}>
          <Text style={styles.primaryText}>{content.action}</Text>
          <Icon name="arrow-right" size={16} color={dark ? '#1D1D1F' : '#FFFFFF'} />
        </Pressable>
        <Text style={styles.purchaseHint}>Choose your Plus plan in the next step.</Text>
        <Pressable accessibilityRole="button" accessibilityLabel={allowCredits ? 'Buy credits instead' : 'Not now'} onPress={allowCredits ? buyCredits : onClose}
          style={({ hovered, pressed }) => [styles.secondary, (hovered || pressed) && styles.hover]}>
          <Text style={styles.secondaryText}>{allowCredits ? 'Buy credits instead' : 'Not now'}</Text>
        </Pressable>
      </View>
    </View>
  );
}

PlusFeatureModal.modalOptions = { width: 520, height: 680, heightMobile: [700, '96%'], background: 'primary' };

const createStyles = (dark, compact) => {
  const ink = dark ? '#F5F5F7' : '#1D1D1F';
  const muted = dark ? '#A5A5AD' : '#68686F';
  const subtle = dark ? '#303033' : '#F5F5F7';
  return StyleSheet.create({
    container: { flex: 1, minHeight: 0, backgroundColor: dark ? '#1C1C1E' : '#FFFFFF' },
    header: { paddingHorizontal: 22, paddingTop: 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    badge: { paddingVertical: 7, paddingHorizontal: 11, borderRadius: 20, backgroundColor: subtle },
    badgeText: { color: ink, fontSize: 10, fontWeight: '600', letterSpacing: 1.4 },
    close: { width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center' },
    hover: { backgroundColor: subtle },
    scroll: { flex: 1, minHeight: 0 },
    body: { paddingHorizontal: compact ? 26 : 44, paddingBottom: 16, alignItems: 'center' },
    artwork: { height: 80, width: '100%', alignItems: 'center', justifyContent: 'center' },
    artworkTile: { width: 64, height: 64, backgroundColor: subtle, borderRadius: 20, alignItems: 'center', justifyContent: 'center' },
    wave: { flexDirection: 'row', gap: 5, alignItems: 'center', justifyContent: 'center', height: 80 },
    waveBar: { width: 5, borderRadius: 3, backgroundColor: ink },
    title: { color: ink, fontSize: compact ? 30 : 34, fontWeight: '600', letterSpacing: -1.1, lineHeight: compact ? 35 : 39, textAlign: 'center' },
    description: { color: muted, fontSize: 15, lineHeight: 22, textAlign: 'center', marginTop: 14, maxWidth: 370 },
    benefits: { width: '100%', gap: 16, marginTop: 22, marginBottom: 16 },
    benefit: { flexDirection: 'row', alignItems: 'center', gap: 14 },
    benefitIcon: { width: 38, height: 38, alignItems: 'center', justifyContent: 'center', borderRadius: 12, backgroundColor: subtle, flexShrink: 0 },
    benefitCopy: { flex: 1, gap: 3 },
    benefitTitle: { color: ink, fontSize: 14, fontWeight: '600', lineHeight: 19 },
    detail: { color: muted, fontSize: 12, lineHeight: 18 },
    included: { width: '100%', borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: dark ? '#38383C' : '#E8E8ED', paddingTop: 18, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
    includedText: { color: muted, fontSize: 11, lineHeight: 17, flexShrink: 1 },
    note: { color: muted, textAlign: 'center', fontSize: 11, lineHeight: 16, marginTop: 6 },
    footer: { paddingHorizontal: compact ? 26 : 44, paddingTop: 8, flexShrink: 0 },
    primary: { minHeight: 52, borderRadius: 16, backgroundColor: ink, paddingVertical: 15, paddingHorizontal: 20, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 12 },
    primaryText: { color: dark ? '#1D1D1F' : '#FFFFFF', fontSize: 15, fontWeight: '600', lineHeight: 21, flexShrink: 1, textAlign: 'center' },
    purchaseHint: { color: muted, fontSize: 11, lineHeight: 16, textAlign: 'center', marginTop: 10 },
    secondary: { minHeight: 40, alignItems: 'center', justifyContent: 'center', borderRadius: 12, marginTop: 2 },
    secondaryText: { color: muted, fontSize: 13, lineHeight: 18 },
  });
};
